import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { HardHat, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SupervisorProgressBars — iter-78 pt 12.
 *
 * Real-time completion cockpit for Supervisor / RM / CEO. Polls
 * /api/ops/crew-progress every 60s and shows a horizontal bar per
 * crew_leader: `Crew A · 3,500 / 5,000 units · 70%`.
 *
 * At-risk crews (< 40% by the mid-shift mark) get a red pulse.
 */
export function SupervisorProgressBars({ user }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/ops/crew-progress`, { credentials: 'include' });
        if (!cancelled && r.ok) setRows((await r.json()).crews || []);
      } catch (_) { /* silent */ }
      if (!cancelled) setLoading(false);
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <Card
      className="bg-zinc-900 border-2 border-[#C9A962]/50 p-5 mb-2"
      data-testid="supervisor-progress-bars"
    >
      <div className="flex items-center justify-between mb-3">
        <h3
          className="font-bold text-lg flex items-center text-white"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          <HardHat className="h-5 w-5 mr-2 text-[#C9A962]" />
          CREW PROGRESS · TONIGHT
        </h3>
        <Badge className="bg-[#C9A962] text-black text-[10px] font-bold">
          {rows.length} crew{rows.length === 1 ? '' : 's'}
        </Badge>
      </div>
      {loading ? (
        <p className="text-sm text-zinc-500 italic">Loading crews…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-zinc-500 italic">No crew leaders in your scope yet.</p>
      ) : (
        <div className="space-y-3" data-testid="progress-list">
          {rows.map((r) => {
            const pct = r.percent || 0;
            const atRisk = pct < 40 && r.scheduled_units > 0;
            const barColor =
              pct >= 80 ? '#16a34a'
              : pct >= 50 ? '#f59e0b'
              : pct >= 25 ? '#ea580c'
              : '#dc2626';
            return (
              <div key={r.crew_leader_id} className="space-y-1" data-testid={`progress-row-${r.crew_leader_id}`}>
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-bold text-white truncate">{r.name}</span>
                    <span className="text-zinc-500 font-mono">{r.badge}</span>
                    <span className="text-zinc-400 flex items-center gap-1">
                      <Users className="h-3 w-3" /> {r.agents_active} on shift
                    </span>
                    {atRisk && (
                      <Badge className="bg-red-600 text-white text-[9px] font-black animate-pulse">
                        AT RISK
                      </Badge>
                    )}
                  </div>
                  <div className="text-zinc-300 font-mono flex-shrink-0">
                    <span className="text-white font-bold">{r.completed_units.toLocaleString()}</span>
                    <span className="text-zinc-500"> / {r.scheduled_units.toLocaleString()} units</span>
                    <span className="ml-2" style={{ color: barColor }}>{pct}%</span>
                  </div>
                </div>
                <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
                  <div
                    className="h-full transition-all"
                    style={{ width: `${Math.max(2, pct)}%`, backgroundColor: barColor }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

export default SupervisorProgressBars;
